import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, CheckCircle2, MessageSquareText, Users, Download, Mail, AlertCircle, Award } from "lucide-react";

function OverdueBadge({ days, testId }) {
  if (!days) return null;
  return (
    <Badge data-testid={testId} className="bg-rag-red text-white border-0 gap-1 hover:bg-rag-red">
      <AlertCircle className="h-3 w-3" />Overdue {days}d
    </Badge>
  );
}

function ScoreBadge({ m }) {
  if (!m.has_quiz) return <span className="text-xs text-muted-foreground">No quiz</span>;
  if (m.best_score === null || m.best_score === undefined) return <Badge variant="outline" className="text-muted-foreground">Not attempted</Badge>;
  const passed = m.best_score >= 50;
  return <Badge variant="outline" className={passed ? "text-rag-green border-rag-green/40" : "text-rag-red border-rag-red/40"} data-testid={`score-${m.lesson_id}`}>{m.best_score}%{m.attempts > 1 ? ` · ${m.attempts} tries` : ""}</Badge>;
}

export default function TrainingReport() {
  const [report, setReport] = useState([]);
  const [open, setOpen] = useState({});

  useEffect(() => { api.get("/manager/training-report").then((r) => setReport(r.data)).catch(() => {}); }, []);
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));

  const download = async (fmt) => {
    try {
      const res = await api.get(`/manager/training-report/export?fmt=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `training-report.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const emailReport = async () => {
    try {
      const { data } = await api.post("/manager/training-report/email");
      toast.success(`Report emailed to ${data.to}`);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Training report" subtitle="Each team member's quiz scores and reflections — ready for supervision and CQC evidence.">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-full" data-testid="report-csv-btn" onClick={() => download("csv")}><Download className="h-4 w-4 mr-2" />CSV</Button>
          <Button variant="outline" className="rounded-full" data-testid="report-pdf-btn" onClick={() => download("pdf")}><Download className="h-4 w-4 mr-2" />PDF</Button>
          <Button className="rounded-full bg-brand hover:bg-brand-hover text-white" data-testid="report-email-btn" onClick={emailReport}><Mail className="h-4 w-4 mr-2" />Email me this</Button>
        </div>
      </PageHeader>
      <div data-testid="certificates-notice" className="mb-4 flex items-start gap-3 rounded-xl border border-cream-deep bg-cream-card px-4 py-3">
        <Award className="h-5 w-5 text-coral shrink-0 mt-0.5" />
        <div>
          <div className="font-heading font-bold text-brand text-sm">Individual Training Certificates — Available on Request</div>
          <p className="text-sm text-muted-foreground">Individual certificates of completion are available for staff who successfully complete KOHC oral healthcare training. Email <a href="mailto:jane@kohc.co.uk?subject=Individual%20training%20certificate%20request" className="text-brand font-medium hover:underline" data-testid="certificates-request-link">jane@kohc.co.uk</a> with the staff member's name and we'll send it over.</p>
        </div>
      </div>
      {(() => {
        const overdueStaff = report.filter((r) => r.enrollments.some((e) => e.overdue_days));
        return overdueStaff.length > 0 && (
          <div data-testid="overdue-summary" className="mb-4 flex items-center gap-2 rounded-xl border border-rag-red/30 bg-rag-red/5 px-4 py-3 text-sm text-rag-red font-medium">
            <AlertCircle className="h-4 w-4" />{overdueStaff.length} team member{overdueStaff.length === 1 ? " has" : "s have"} overdue training
          </div>
        );
      })()}
      {report.length === 0 && (
        <div className="bg-cream-card border border-cream-deep rounded-xl p-10 text-center text-muted-foreground" data-testid="report-empty">
          <Users className="h-8 w-8 mx-auto mb-3 opacity-50" />No staff training activity yet. Add staff and assign courses to see progress here.
        </div>
      )}
      <div className="space-y-3">
        {report.map((row) => {
          const s = row.staff;
          const totalMods = row.enrollments.reduce((n, e) => n + e.modules.length, 0);
          const passedMods = row.enrollments.reduce((n, e) => n + e.modules.filter((m) => m.completed).length, 0);
          const maxOverdue = Math.max(0, ...row.enrollments.map((e) => e.overdue_days || 0));
          return (
            <div key={s.id} className={`bg-cream-card border rounded-xl overflow-hidden ${maxOverdue ? "border-rag-red/50 border-l-4 border-l-rag-red" : "border-cream-deep"}`} data-testid={`report-staff-${s.id}`}>
              <button onClick={() => toggle(s.id)} className="w-full flex items-center gap-3 p-4 text-left hover:bg-cream/50 transition-colors">
                {open[s.id] ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground">{s.name} <span className="text-muted-foreground text-sm font-normal">· {s.job_role || "Care worker"}</span></p>
                  <p className="text-xs text-muted-foreground truncate">{s.email}</p>
                </div>
                <OverdueBadge days={maxOverdue} testId={`overdue-staff-${s.id}`} />
                <Badge variant="outline">{row.enrollments.length} course{row.enrollments.length === 1 ? "" : "s"}</Badge>
                <Badge variant="outline" className={passedMods === totalMods && totalMods > 0 ? "text-rag-green border-rag-green/40" : ""}>{passedMods}/{totalMods} modules passed</Badge>
              </button>

              {open[s.id] && (
                <div className="border-t border-cream-deep p-4 space-y-4">
                  {row.enrollments.length === 0 && <p className="text-sm text-muted-foreground">No courses assigned.</p>}
                  {row.enrollments.map((e) => (
                    <div key={e.course_id}>
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-brand">{e.course_title}</h4>
                        <Badge variant="outline" className={e.status === "completed" ? "text-rag-green border-rag-green/40" : ""}>{e.status === "completed" ? "Completed" : `${e.progress}%`}</Badge>
                        <OverdueBadge days={e.overdue_days} testId={`overdue-course-${s.id}-${e.course_id}`} />
                      </div>
                      <div className="space-y-1.5">
                        {e.modules.map((m) => (
                          <div key={m.lesson_id} className="flex flex-wrap items-center gap-3 py-1.5 border-b border-cream last:border-0">
                            {m.completed ? <CheckCircle2 className="h-4 w-4 text-rag-green flex-shrink-0" /> : <span className="h-4 w-4 flex-shrink-0 rounded-full border border-cream-deep" />}
                            <span className="flex-1 min-w-0 text-sm text-foreground">{m.title}</span>
                            <ScoreBadge m={m} />
                            {m.reflection && (
                              <div className="w-full flex items-start gap-2 mt-1 ml-7 text-xs text-muted-foreground bg-cream rounded-lg p-2" data-testid={`reflection-${m.lesson_id}`}>
                                <MessageSquareText className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-brand" /><span className="italic">"{m.reflection}"</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
